// audio-player-dialog.component.ts
import { Component, Inject, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSliderModule } from '@angular/material/slider';

@Component({
  selector: 'app-audio-player-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatSliderModule
  ],
  template: `
    <h2 mat-dialog-title>
      <span>{{ data.title }}</span>
      <button mat-icon-button class="close-button" (click)="dialogRef.close()">
        <mat-icon>close</mat-icon>
      </button>
    </h2>
    
    <mat-dialog-content>
      <div class="audio-player-container">
        <div class="call-info">
          <div class="info-item">
            <span class="label">Phone:</span>
            <span class="value">{{ data.call.recipient_phone_number }}</span>
          </div>
          <div class="info-item">
            <span class="label">Direction:</span>
            <span class="value">{{ data.call.direction | titlecase }}</span>
          </div>
          <div class="info-item">
            <span class="label">Duration:</span>
            <span class="value">{{ formatDuration(data.call.duration) }}</span>
          </div>
          <div class="info-item">
            <span class="label">Status:</span>
            <span class="value">{{ data.call.call_status | titlecase }}</span>
          </div>
        </div>
        
        <audio #audioPlayer class="audio-element" preload="metadata" (timeupdate)="updateProgress()" (loadedmetadata)="onMetadataLoaded()">
          <source [src]="data.audioUrl" type="audio/mpeg">
          Your browser does not support the audio element.
        </audio>
        
        <div class="player-controls">
          <button mat-icon-button (click)="togglePlayback()" color="primary" class="play-button">
            <mat-icon>{{ isPlaying ? 'pause' : 'play_arrow' }}</mat-icon>
          </button>
          
          <div class="time-display">{{ formatTime(currentTime) }}</div>
          
          <mat-progress-bar 
            class="progress-bar" 
            [value]="progressPercentage" 
            color="primary"
            (click)="seekAudio($event)">
          </mat-progress-bar>
          
          <div class="time-display">{{ formatTime(duration) }}</div>
          
          <button mat-icon-button (click)="toggleMute()" [color]="isMuted ? 'warn' : ''">
            <mat-icon>{{ getVolumeIcon() }}</mat-icon>
          </button>
          
          <!-- Updated slider implementation with display volume (0-100) -->
          <mat-slider class="volume-slider" [disabled]="isMuted">
            <input matSliderThumb [(ngModel)]="displayVolume" (ngModelChange)="updateVolumeFromDisplay($event)" 
                 min="0" max="100" step="1">
          </mat-slider>
        </div>
      </div>
    </mat-dialog-content>
    
    <mat-dialog-actions align="end">
      <button mat-button (click)="downloadRecording()">
        <mat-icon>file_download</mat-icon>
        Download
      </button>
      <button mat-button [mat-dialog-close]="true" cdkFocusInitial>Close</button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host {
      display: block;
      max-width: 500px;
      width: 100%;
    }

    h2.mat-dialog-title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      color: var(--form-text-primary);
    }

    .audio-player-container {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .call-info {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      background-color: var(--form-card-bg, #f5f5f5);
      border-radius: 8px;
      padding: 12px;
      margin-bottom: 8px;
      border: 1px solid var(--form-border-color, rgba(0, 0, 0, 0.12));

      .info-item {
        display: flex;
        flex-direction: column;

        .label {
          font-size: 12px;
          color: var(--form-text-secondary, #666);
        }

        .value {
          font-weight: 500;
          color: var(--form-text-primary, #333);
        }
      }
    }

    .audio-element {
      width: 100%;
      display: none;
    }

    .player-controls {
      display: flex;
      align-items: center;
      gap: 8px;

      .play-button {
        transform: scale(1.2);
      }

      .progress-bar {
        flex: 1;
        height: 8px;
        cursor: pointer;
        border-radius: 4px;
      }

      .time-display {
        font-size: 12px;
        font-family: monospace;
        color: var(--form-text-secondary, #666);
        min-width: 40px;
      }

      .volume-slider {
        width: 80px;
      }
    }

    ::ng-deep .mat-mdc-dialog-container {
      --mdc-dialog-container-color: var(--form-card-bg, #fff);
    }

    @media (max-width: 480px) {
      .call-info {
        grid-template-columns: 1fr;
      }

      .player-controls {
        flex-wrap: wrap;

        .volume-slider {
          width: 60px;
        }
      }
    }
  `]
})
export class AudioPlayerDialogComponent implements OnInit, OnDestroy {
  @ViewChild('audioPlayer') audioPlayerRef!: ElementRef<HTMLAudioElement>;
  
  isPlaying: boolean = false;
  isMuted: boolean = false;
  volume: number = 0.7; // Internal volume (0-1 scale)
  displayVolume: number = 70; // UI display volume (0-100 scale)
  currentTime: number = 0;
  duration: number = 0;
  progressPercentage: number = 0;
  
  private updateInterval: any;
  
  constructor(
    public dialogRef: MatDialogRef<AudioPlayerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { 
      audioUrl: string; 
      call: any;
      title: string;
    }
  ) {}
  
  ngOnInit(): void {
    // Setup interval to update UI even when timeupdate event isn't firing
    this.updateInterval = setInterval(() => {
      if (this.audioPlayer && this.isPlaying) {
        this.updateProgress();
      }
    }, 500);
  }
  
  ngOnDestroy(): void {
    clearInterval(this.updateInterval);
    
    // Make sure to pause audio when dialog closes
    if (this.audioPlayer) {
      this.audioPlayer.pause();
    }
  }
  
  get audioPlayer(): HTMLAudioElement {
    return this.audioPlayerRef?.nativeElement;
  }
  
  onMetadataLoaded(): void {
    if (!this.audioPlayer) return;
    
    this.duration = this.audioPlayer.duration || 0;
    
    // Initialize volume to 0.7 (70%)
    this.volume = 0.7;
    this.audioPlayer.volume = this.volume;
    this.displayVolume = Math.round(this.volume * 100);
    
    this.updateProgress();
  }
  
  togglePlayback(): void {
    if (!this.audioPlayer) return;
    
    if (this.isPlaying) {
      this.audioPlayer.pause();
    } else {
      this.audioPlayer.play().catch(error => {
        console.error('Error playing audio:', error);
      });
    }
    
    this.isPlaying = !this.isPlaying;
  }
  
  toggleMute(): void {
    if (!this.audioPlayer) return;
    
    this.audioPlayer.muted = !this.audioPlayer.muted;
    this.isMuted = this.audioPlayer.muted;
  }
  
  // Convert 0-100 display value to 0-1 for audio element
  updateVolumeFromDisplay(displayValue: number): void {
    // Ensure value is within 0-100 range
    const normalizedDisplayValue = Math.max(0, Math.min(100, displayValue));
    this.displayVolume = normalizedDisplayValue;
    
    // Convert to 0-1 range for audio element
    const actualVolume = normalizedDisplayValue / 100;
    this.updateVolume(actualVolume);
  }
  
  // Update actual volume (0-1 scale)
  updateVolume(value: number): void {
    if (!this.audioPlayer) return;
    
    // Ensure value is within 0-1 range
    const normalizedValue = Math.max(0, Math.min(1, value));
    
    // Update internal state
    this.volume = normalizedValue;
    this.audioPlayer.volume = normalizedValue;
    
    // If volume is set to something > 0, unmute if currently muted
    if (normalizedValue > 0 && this.isMuted) {
      this.toggleMute();
    }
  }
  
  updateProgress(): void {
    if (!this.audioPlayer) return;
    
    this.currentTime = this.audioPlayer.currentTime || 0;
    this.duration = this.audioPlayer.duration || 0;
    
    if (this.duration > 0) {
      this.progressPercentage = (this.currentTime / this.duration) * 100;
    } else {
      this.progressPercentage = 0;
    }
    
    // Check if playback has ended
    if (this.currentTime >= this.duration && this.duration > 0) {
      this.isPlaying = false;
    }
  }
  
  seekAudio(event: MouseEvent): void {
    if (!this.audioPlayer || !this.duration) return;
    
    const progressBar = event.currentTarget as HTMLElement;
    const rect = progressBar.getBoundingClientRect();
    const clickPosition = event.clientX - rect.left;
    const percentage = clickPosition / rect.width;
    
    // Ensure percentage is within 0-1 range
    const normalizedPercentage = Math.max(0, Math.min(1, percentage));
    
    // Set the current time based on click position
    this.audioPlayer.currentTime = normalizedPercentage * this.duration;
    this.updateProgress();
  }
  
  getVolumeIcon(): string {
    if (this.isMuted) return 'volume_off';
    if (this.volume > 0.5) return 'volume_up';
    if (this.volume > 0) return 'volume_down';
    return 'volume_mute';
  }
  
  formatTime(seconds: number): string {
    if (isNaN(seconds) || !isFinite(seconds)) return '00:00';
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  formatDuration(seconds?: number): string {
    if (!seconds) return '00:00';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  
  downloadRecording(): void {
    if (!this.data.audioUrl) return;
    
    // Create an anchor element for downloading
    const a = document.createElement('a');
    a.href = this.data.audioUrl;
    a.download = `Call_${this.data.call.id}_${new Date(this.data.call.start_time).toLocaleDateString().replace(/\//g, '-')}.mp3`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}